/*
 * Copyright 2020 Adobe. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
import getCss from 'dom-helpers/css';
import contains from 'dom-helpers/contains';
import getOffsetParent from 'dom-helpers/offsetParent';
import getScrollLeft from 'dom-helpers/scrollLeft';
import getScrollTop from 'dom-helpers/scrollTop';
import ownerDocument from 'dom-helpers/ownerDocument';

/** 
** This function recursively checks if the node belongs to a shadow root.
*/
function findShadowRoot(node) {
  if (!ownerDocument(node).documentElement.attachShadow) return null;

  if (node.getRootNode) { // Can find the shadow root otherwise it'll return the document
    const root = node.getRootNode()
    return root instanceof ShadowRoot ? root : null
  }

  if (node instanceof ShadowRoot) return node;
  if (!node.parentNode) return null; // when we don't find a shadow root
  return findShadowRoot(node.parentNode)
}

/** 
** These functions are forked from dom-helpers as they have issues with shadow DOM 
** Issue on dom-helpers repo: https://github.com/react-bootstrap/dom-helpers/issues/172
** The only modification we've performed on them is how they check for the node being contained in the DOM
** previously they checked for ownerdocument.contains, now they take shadow DOM into account if present
*/
function nodeName(node) {
  return node.nodeName && node.nodeName.toLowerCase();
}

function getOffset(node) {
  const doc = ownerDocument(node);

  let box = {top: 0, left: 0, height: 0, width: 0};

  const docElem = doc?.documentElement; // we still want to use docElem to obtain our dimensions but use the actual parent node to check whether it contains the node
  const parent = doc?.documentElement;
  if (!parent || !contains(parent, node)) return box;
  if (node.getBoundingClientRect !== undefined) box = node.getBoundingClientRect();

  box = {
    top: box.top + getScrollTop(docElem) - (docElem.clientTop || 0),
    left: box.left + getScrollLeft(docElem) - (docElem.clientLeft || 0),
    width: box.width,
    height: box.height
  };
  return box;
}

function getPosition(node, offsetParent) {
  let parentOffset = {
    top: 0,
    left: 0,
  };
  let offset; // Fixed elements are offset from window (parentOffset = {top:0, left: 0},
  // because it is its only offset parent

  if (getComputedStyle(node).position === 'fixed') {
    offset = node.getBoundingClientRect();
  } else {
    const parent = offsetParent || getOffsetParent(node)
    offset = getOffset(node)

    if (nodeName(parent) !== 'html') parentOffset = getOffset(parent)
    const borderTop = getComputedStyle(parent).borderTopWidth || 0;
    parentOffset.top += parseInt(borderTop, 10) - getScrollTop(parent) || 0

    const borderLeft = getComputedStyle(parent).borderLeftWidth || 0
    parentOffset.left += parseInt(borderLeft, 10) - getScrollLeft(parent) || 0
  }

  const marginTop = getComputedStyle(node).marginTop || 0;
  const marginLeft =  getComputedStyle(node).marginLeft || 0;
  // Subtract parent offsets and node margins
  return {
    ...offset,
    top: offset.top - parentOffset.top - (parseInt(marginTop, 10) || 0),
    left: offset.left - parentOffset.left - (parseInt(marginLeft, 10) || 0),
  }
}

const AXIS = {
    top: 'top',
    bottom: 'top',
    left: 'left',
    right: 'left'
};
const FLIPPED_DIRECTION = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left'
};
const CROSS_AXIS = {
    top: 'left',
    left: 'top'
};
const AXIS_SIZE = {
    top: 'height',
    left: 'width'
};
const PARSED_PLACEMENT_CACHE = {};
function getContainerDimensions(containerNode) {
  const windowObject = ownerDocument(containerNode).defaultView ?? window;
    const visualViewport = typeof windowObject !== 'undefined' && windowObject.visualViewport;
    var _a, _b;
    let width = 0, height = 0, top = 0, left = 0;
    let scroll = {};
    if (containerNode.tagName === 'BODY') {
      width = (_a = visualViewport === null || visualViewport === void 0 ? void 0 : visualViewport.width) !== null && _a !== void 0 ? _a : ownerDocument(containerNode).documentElement.clientWidth;
      height = (_b = visualViewport === null || visualViewport === void 0 ? void 0 : visualViewport.height) !== null && _b !== void 0 ? _b : ownerDocument(containerNode).documentElement.clientHeight;
        scroll.top =
            getScrollTop(ownerDocument(containerNode).documentElement) ||
                getScrollTop(containerNode);
        scroll.left =
            getScrollLeft(ownerDocument(containerNode).documentElement) ||
                getScrollLeft(containerNode);
    }
    else {
        ({ width, height, top, left } = getOffset(containerNode));
        scroll.top = getScrollTop(containerNode);
        scroll.left = getScrollLeft(containerNode);
    }
    return { width, height, scroll, top, left };
}
function getScroll(node) {
    return {
        top: node.scrollTop,
        left: node.scrollLeft,
        width: node.scrollWidth,
        height: node.scrollHeight
    };
}
function getDelta(axis, offset, size, containerDimensions, padding) {
    let containerScroll = containerDimensions.scroll[axis];
    let containerHeight = containerDimensions[AXIS_SIZE[axis]];
    let startEdgeOffset = offset - padding - containerScroll;
    let endEdgeOffset = offset + padding - containerScroll + size;
    if (startEdgeOffset < 0) {
        return -startEdgeOffset;
    }
    else if (endEdgeOffset > containerHeight) {
        return Math.max(containerHeight - endEdgeOffset, -startEdgeOffset);
    }
    else {
        return 0;
    }
}
function getMargins(node) {
    let style = window.getComputedStyle(node);
    return {
        top: parseInt(style.marginTop, 10) || 0,
        bottom: parseInt(style.marginBottom, 10) || 0,
        left: parseInt(style.marginLeft, 10) || 0,
        right: parseInt(style.marginRight, 10) || 0
    };
}
function parsePlacement(input) {
    if (PARSED_PLACEMENT_CACHE[input]) {
        return PARSED_PLACEMENT_CACHE[input];
    }
    let [placement, crossPlacement] = input.split(' ');
    let axis = AXIS[placement] || 'right';
    let crossAxis = CROSS_AXIS[axis];
    if (!AXIS[crossPlacement]) {
        crossPlacement = 'center';
    }
    let size = AXIS_SIZE[axis];
    let crossSize = AXIS_SIZE[crossAxis];
    PARSED_PLACEMENT_CACHE[input] = { placement, crossPlacement, axis, crossAxis, size, crossSize };
    return PARSED_PLACEMENT_CACHE[input];
}
function computePosition(childOffset, boundaryDimensions, overlaySize, placementInfo, offset, crossOffset, containerOffsetWithBoundary, isContainerPositioned) {
    let { placement, crossPlacement, axis, crossAxis, size, crossSize } = placementInfo;
    let position = {};
    // button position
    position[crossAxis] = childOffset[crossAxis];
    if (crossPlacement === 'center') {
        //  + (button size / 2) - (overlay size / 2)
        // at this point the overlay center should match the button center
        position[crossAxis] += (childOffset[crossSize] - overlaySize[crossSize]) / 2;
    }
    else if (crossPlacement !== crossAxis) {
        //  + (button size) - (overlay size)
        // at this point the overlay bottom should match the button bottom
        position[crossAxis] += (childOffset[crossSize] - overlaySize[crossSize]);
    } /* else {
      the overlay top should match the button top
    } */
    // add the crossOffset from props
    position[crossAxis] += crossOffset;

    if (crossPlacement === 'center') {
      // this is button center position - the overlay size + half of the button to align bottom of overlay with button center
      let minViablePosition = childOffset[crossAxis] + (childOffset[crossSize] / 2) - overlaySize[crossSize];
      // this is button position of center, aligns top of overlay with button center
      let maxViablePosition = childOffset[crossAxis] + (childOffset[crossSize] / 2);
      // clamp it into the range of the min/max positions
      position[crossAxis] = Math.min(Math.max(minViablePosition, position[crossAxis]), maxViablePosition);
    }

    // Floor these so the position isn't placed on a partial pixel, only whole pixels. Shouldn't matter if it was floored or ceiled, so chose one.
    if (placement === axis) {
        // If the container is positioned (non-static), then we use the container's actual
        // height, as `bottom` will be relative to this height.  But if the container is static,
        // then it can only be the `document.body`, and `bottom` will be relative to _its_
        // container, which should be as large as boundaryDimensions.
        const containerHeight = (isContainerPositioned ? containerOffsetWithBoundary[size] : boundaryDimensions[size]);
        position[FLIPPED_DIRECTION[axis]] = Math.floor(containerHeight - childOffset[axis] + offset);
    }
    else {
        position[axis] = Math.floor(childOffset[axis] + childOffset[size] + offset);
    }
    return position;
}
function getMaxHeight(position, boundaryDimensions, containerOffsetWithBoundary, childOffset, margins, padding) {
    return position.top != null
        // We want the distance between the top of the overlay to the bottom of the boundary
        ? Math.max(0, (boundaryDimensions.height + boundaryDimensions.top + boundaryDimensions.scroll.top) // this is the bottom of the boundary
            - (containerOffsetWithBoundary.top + position.top) // this is the top of the overlay
            - (margins.top + margins.bottom + padding) // save additional space for margin and padding
        )
        // We want the distance between the top of the trigger to the top of the boundary
        : Math.max(0, (childOffset.top + containerOffsetWithBoundary.top) // this is the top of the trigger
            - (boundaryDimensions.top + boundaryDimensions.scroll.top) // this is the top of the boundary
            - (margins.top + margins.bottom + padding) // save additional space for margin and padding
        );
}

function getAvailableSpace(boundaryDimensions, containerOffsetWithBoundary, childOffset, margins, padding, placementInfo) {
    let { placement, axis, size } = placementInfo;
    if (placement === axis) {
        return Math.max(0, childOffset[axis] -
            boundaryDimensions[axis] -
            boundaryDimensions.scroll[axis] -
            margins[axis] -
            margins[FLIPPED_DIRECTION[axis]] -
            padding);
    }


  return Math.max(0,
        boundaryDimensions[size] +
        boundaryDimensions.scroll[axis] -
        childOffset[axis] -
        childOffset[size] -
        margins[axis] -
        margins[FLIPPED_DIRECTION[axis]] -
        padding);
}

export function calculatePositionInternal(placementInput, childOffset, overlaySize, scrollSize, margins, padding, flip, boundaryDimensions, containerOffsetWithBoundary, offset, crossOffset, isContainerPositioned) {
    let placementInfo = parsePlacement(placementInput);
    let { size, crossAxis, crossSize, placement, crossPlacement } = placementInfo;
    let position = computePosition(childOffset, boundaryDimensions, overlaySize, placementInfo, offset, crossOffset, containerOffsetWithBoundary, isContainerPositioned);
    let normalizedOffset = offset;
    let space = getAvailableSpace(boundaryDimensions, containerOffsetWithBoundary, childOffset, margins, padding + offset, placementInfo);
    let fit = scrollSize[size] <= space;
    // Check if the scroll size of the overlay is greater than the available space to determine if we need to flip
    if (flip && scrollSize[size] > space) {
        let flippedPlacementInfo = parsePlacement(`${FLIPPED_DIRECTION[placement]} ${crossPlacement}`);
        let flippedPosition = computePosition(childOffset, boundaryDimensions, overlaySize, flippedPlacementInfo, offset, crossOffset, containerOffsetWithBoundary, isContainerPositioned);
        let flippedSpace = getAvailableSpace(boundaryDimensions, containerOffsetWithBoundary, childOffset, margins, padding + offset, flippedPlacementInfo);
        fit = scrollSize[size] <= flippedSpace;
        // If the available space for the flipped position is greater than the original available space, flip.
        if (flippedSpace > space) {
            placementInfo = flippedPlacementInfo;
            position = flippedPosition;
            normalizedOffset = offset;
        }
    }
    let delta = getDelta(crossAxis, position[crossAxis], overlaySize[crossSize], boundaryDimensions, padding);
    position[crossAxis] += delta;
    let maxHeight = getMaxHeight(position, boundaryDimensions, containerOffsetWithBoundary, childOffset, margins, padding);
    // overlaySize.height = Math.min(overlaySize.height, maxHeight);
    position = computePosition(childOffset, boundaryDimensions, overlaySize, placementInfo, normalizedOffset, crossOffset, containerOffsetWithBoundary, isContainerPositioned);
    delta = getDelta(crossAxis, position[crossAxis], overlaySize[crossSize], boundaryDimensions, padding);
    position[crossAxis] += delta;
    let arrowPosition = {};
    arrowPosition[crossAxis] = (childOffset[crossAxis] - position[crossAxis] + childOffset[crossSize] / 2);
    return {
        position,
        maxHeight: maxHeight,
        arrowOffsetLeft: arrowPosition.left,
        arrowOffsetTop: arrowPosition.top,
        placement: placementInfo.placement,
        crossPlacement: placementInfo.crossPlacement,
        fit
    };
}
/**
 * Determines where to place the overlay with regards to the target and the position of an optional indicator.
 */
export function calculatePosition(opts) {
    let { placement, targetNode, overlayNode, scrollNode, padding, shouldFlip, boundaryElement, offset, crossOffset } = opts;

    let container = boundaryElement;

    let isBodyContainer = container.tagName === 'BODY';
    const containerPositionStyle = window.getComputedStyle(container).position;
    let isContainerPositioned = !!containerPositionStyle && containerPositionStyle !== 'static';
    let childOffset = isBodyContainer ? getOffset(targetNode) : getPosition(targetNode, container);
    if (!isBodyContainer) {
        childOffset.top += parseInt(getCss(targetNode, 'marginTop'), 10) || 0;
        childOffset.left += parseInt(getCss(targetNode, 'marginLeft'), 10) || 0;
    }
    let overlaySize = getOffset(overlayNode);
    let margins = getMargins(overlayNode);
    overlaySize.width += margins.left + margins.right;
    overlaySize.height += margins.top + margins.bottom;
    let scrollSize = getScroll(scrollNode);
    let boundaryDimensions = getContainerDimensions(boundaryElement);

    if(boundaryElement.tagName !== 'BODY') {
      if(process.env.NODE_ENV !== 'production' && window.getComputedStyle(boundaryElement).position === "static") {
        console.warn(
          "[Sail] Layer is trying to position itself relative to a scrollable element " +
          `(<${boundaryElement.tagName.toLowerCase()}>) ` +
          "but scrollable element does not have `position: relative`. Popovers, tooltips, and more " +
          "might be positioned wrongly. To fix this, add `position: relative` to the scrollable " +
          "element they are inside of."
        )
      }

      // Remove any scroll info from the dimensions. We want to ignore this when
      // positioning relative to a scrollable container because it will be
      // positioned to the container outside of the scrollable area. We only
      // want to take scrolling into account when attaching to body because it
      // will be relative to the whole scrollable area
      boundaryDimensions.scroll = { top: 0, left: 0 };
    }

    let containerOffsetWithBoundary = getOffset(container)
    return calculatePositionInternal(placement, childOffset, overlaySize, scrollSize, margins, padding, shouldFlip, boundaryDimensions, containerOffsetWithBoundary, offset, crossOffset, isContainerPositioned);
}
